import { Check, X } from 'lucide-react';
import { StoryStep } from './StoryStep';
import { fmtPct, fmtNum, fmtMoney, type AdStatus } from './story-utils';
import type { AiInsight } from '@/types/sem';

interface AdStepProps {
  headline: string;
  ctr: number;
  clicks: number;
  cpc: number;
  keywords: string[];
  status: AdStatus;
  insight?: AiInsight;
}

export function AdStep({ headline, ctr, clicks, cpc, keywords, status, insight }: AdStepProps) {
  const isOk = status === 'performing';

  return (
    <StoryStep
      label="User Searches & Clicks Ad"
      tag={
        <div className={`story-tg ${isOk ? 'story-tg-ok' : 'story-tg-warn'}`}>
          {isOk ? <Check /> : <X />}
          {isOk ? 'Performing' : 'Underperforming'}
        </div>
      }
      collapsedStats={
        <>
          <div className="story-acc-stat">
            <span className="label">CTR</span>
            <span>{fmtPct(ctr)}</span>
          </div>
          <div className="story-acc-stat">
            <span className="label">Clicks</span>
            <span>{fmtNum(clicks)}</span>
          </div>
        </>
      }
    >
      <div className="story-quote">"{headline}"</div>
      <div className="story-mrow">
        <div className="story-mi">
          <span className="story-mi-l">CTR</span>
          <span className="story-mi-v">{fmtPct(ctr)}</span>
        </div>
        <div className="story-mi">
          <span className="story-mi-l">Monthly Clicks</span>
          <span className="story-mi-v">{fmtNum(clicks)}</span>
        </div>
        <div className="story-mi">
          <span className="story-mi-l">Cost / Click</span>
          <span className="story-mi-v">{fmtMoney(cpc)}</span>
        </div>
      </div>
      {keywords.length > 0 && (
        <div className="story-kw">
          <div className="story-kw-lbl">Top Keywords Driving Traffic</div>
          <div className="story-kw-row">
            {keywords.map(kw => (
              <span className="story-kw-p" key={kw}>{kw}</span>
            ))}
          </div>
        </div>
      )}
      {insight?.headline_suggestions && insight.headline_suggestions.length > 0 && (
        <div className="mt-4 pt-4 border-t border-[var(--ed-rule)]">
          <p className="text-[10px] text-[var(--ed-t4)] uppercase tracking-wider mb-2">
            AI Suggestions
          </p>
          <div className="space-y-2">
            {insight.headline_suggestions.map((s, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="shrink-0 text-xs font-medium px-1.5 py-0.5 rounded bg-[var(--ed-amber-bg)] text-[var(--ed-amber)] border border-[rgba(224,168,82,0.12)]">
                  {s.text}
                </span>
                <span className="text-xs text-[var(--ed-t3)]">{s.rationale}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </StoryStep>
  );
}
